import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { DialogContent, DialogClose } from '@radix-ui/react-dialog';
import { MessagesProvider } from '../hooks/useChatbot.js';
import { forwardRef } from 'react';
import { styled, css } from 'styled-components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { AssistantHeader } from './AssistantHeader.js';
import { AnimatePresence } from 'framer-motion';
import MessageForm from './Chatbot/MessageForm.js';
import MessagesList from './Chatbot/MessageList.js';
import { useAssistantStore } from '../stores/assistantStore.js';
import { pick } from 'lodash-es';
import { ExtraPanelSidebar } from '../../resource-detail/components/ExpandedResource/ExtraPanel/Sidebar.js';
import { defaultModalContentProps, Modal } from '@fabric-app/ui/components/Modal/Modal';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { framerAnimationFade } from '@fabric-app/ui/constants/framerAnimations';
import useDeferred from '@fabric-app/hooks/useDeferred';
import { ForcedTheme } from '@fabric-app/ui/theme/ForcedTheme';
import { Tabs } from '@fabric-app/ui/components/Tabs';
import { useExpandedResource } from '../../resources/stores/useExpandedResourceStore.js';
import { IconAssistantTab } from '@fabric-app/icons/old';
import { IconNavigational24Shrink, IconNavigational24Cross } from '@fabric-app/icons/svg';

const FixedSidebar = styled(ExtraPanelSidebar.Root).withConfig({
  displayName: "StandaloneChat__FixedSidebar",
  componentId: "sc-2d34d8bd-0"
})([
  ``,
  ` z-index:125;`
], (p) => !p.isFullscreen && css([
  `position:fixed;right:0.625rem;top:0.625rem;height:calc(100% - 1.25rem) !important;`
]));
const StandaloneChatDesktop = /* @__PURE__ */ forwardRef(({ animateIn = true, chatAssistantOpen }, ref) => {
  const { chatAssistantFullscreen, setChatAssistantFullscreen } = useAssistantStore();
  return /* @__PURE__ */ jsx(MessagesProvider, {
    chatAssistantOpen,
    children: /* @__PURE__ */ jsxs(DialogContent, {
      ...defaultModalContentProps,
      "aria-describedby": void 0,
      children: [
        /* @__PURE__ */ jsx(VisuallyHidden, {
          children: /* @__PURE__ */ jsx(Modal.Title, {
            children: "Ask Fabric Assistant"
          })
        }),
        /* @__PURE__ */ jsx(ForcedTheme, {
          colorScheme: "dark",
          asChild: true,
          children: /* @__PURE__ */ jsx(FixedSidebar, {
            ref,
            ...animateIn && framerAnimationFade,
            isFullscreen: chatAssistantFullscreen,
            "data-assistant-fullscreen": chatAssistantFullscreen,
            children: /* @__PURE__ */ jsx(Tabs.Root, {
              defaultValue: "assistant",
              asChild: true,
              children: /* @__PURE__ */ jsxs(ExtraPanelSidebar.Content, {
                children: [
                  /* @__PURE__ */ jsx(ExtraPanelSidebar.Header, {
                    children: chatAssistantFullscreen ? /* @__PURE__ */ jsxs(Fragment, {
                      children: [
                        /* @__PURE__ */ jsx(ExtraPanelSidebar.FullScreenTitle, {
                          children: "Fabric assistant"
                        }),
                        /* @__PURE__ */ jsxs(ExtraPanelSidebar.FullScreenButtonsContainer, {
                          children: [
                            /* @__PURE__ */ jsx(ExtraPanelSidebar.FullScreenIconButton, {
                              onClick: () => setChatAssistantFullscreen(false),
                              children: /* @__PURE__ */ jsx(IconNavigational24Shrink, {})
                            }),
                            /* @__PURE__ */ jsx(DialogClose, {
                              asChild: true,
                              children: /* @__PURE__ */ jsx(ExtraPanelSidebar.FullScreenIconButton, {
                                children: /* @__PURE__ */ jsx(IconNavigational24Cross, {})
                              })
                            })
                          ]
                        })
                      ]
                    }) : /* @__PURE__ */ jsxs(Fragment, {
                      children: [
                        /* @__PURE__ */ jsx(DialogClose, {
                          asChild: true,
                          children: /* @__PURE__ */ jsx(ExtraPanelSidebar.CloseButton, {})
                        }),
                        /* @__PURE__ */ jsx(Tabs.List, {
                          variant: "flush",
                          children: /* @__PURE__ */ jsxs(Tabs.Trigger, {
                            variant: "apparent",
                            value: "assistant",
                            ignoreState: true,
                            disabled: true,
                            children: [
                              /* @__PURE__ */ jsx(IconAssistantTab, {
                                style: {
                                  width: 20,
                                  height: 20
                                }
                              }),
                              "Ask Fabric Assistant"
                            ]
                          })
                        })
                      ]
                    })
                  }),
                  /* @__PURE__ */ jsxs(Tabs.Content, {
                    value: "assistant",
                    style: {
                      flexGrow: 1,
                      overflow: "hidden",
                      flexDirection: "column"
                    },
                    children: [
                      /* @__PURE__ */ jsx(MessagesList, {}),
                      /* @__PURE__ */ jsx(AnimatePresence, {
                        initial: false,
                        children: /* @__PURE__ */ jsx(MessageForm, {
                          autoFocus: true
                        })
                      })
                    ]
                  })
                ]
              })
            })
          })
        })
      ]
    })
  });
});
StandaloneChatDesktop.displayName = "StandaloneChatDesktop";
const StandaloneChatMobile = /* @__PURE__ */ forwardRef(({ chatAssistantOpen }, ref) => {
  return /* @__PURE__ */ jsx(MessagesProvider, {
    chatAssistantOpen,
    children: /* @__PURE__ */ jsxs(Modal.Content, {
      respectMobileKeyboard: true,
      ref,
      "aria-describedby": void 0,
      gap: "none",
      children: [
        /* @__PURE__ */ jsx(VisuallyHidden, {
          children: /* @__PURE__ */ jsx(Modal.Title, {
            children: "Ask Fabric Assistant"
          })
        }),
        /* @__PURE__ */ jsx(AssistantHeader.Root, {
          children: /* @__PURE__ */ jsx(AssistantHeader.Right, {
            children: /* @__PURE__ */ jsx(Modal.Close, {
              variant: "bg-contrast-reverse-semi-transparent",
              noAutoMargin: true
            })
          })
        }),
        /* @__PURE__ */ jsx(MessagesList, {}),
        /* @__PURE__ */ jsx(AnimatePresence, {
          initial: false,
          children: /* @__PURE__ */ jsx(MessageForm, {
            autoFocus: true
          })
        })
      ]
    })
  });
});
StandaloneChatMobile.displayName = "StandaloneChatMobile";
const StandaloneChat = () => {
  const { isMobileView } = useResponsive();
  const expandedResourceId = useExpandedResource();
  const { chatAssistantOpen, setChatAssistantOpen } = useAssistantStore((s) => pick(s, [
    "chatAssistantOpen",
    "setChatAssistantOpen"
  ]));
  const deferredExpandedResourceId = useDeferred(expandedResourceId);
  const animateIn = deferredExpandedResourceId === null && expandedResourceId === null;
  const Content = isMobileView ? StandaloneChatMobile : StandaloneChatDesktop;
  return /* @__PURE__ */ jsx(Modal.Root, {
    open: chatAssistantOpen && !expandedResourceId,
    onOpenChange: setChatAssistantOpen,
    children: /* @__PURE__ */ jsxs(Modal.Portal, {
      children: [
        /* @__PURE__ */ jsx(Modal.Overlay, {
          visibleOnMobileViewport: true
        }),
        /* @__PURE__ */ jsx(Content, {
          animateIn,
          chatAssistantOpen
        })
      ]
    })
  });
};

export { StandaloneChat };
//# sourceMappingURL=StandaloneChat.js.map
